import React, { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { IonIcon, IonText } from '@ionic/react';
import { closeCircle, addCircleOutline, chevronDownOutline } from 'ionicons/icons';
import './TagsInput.css';
import { useDebounce } from '../../utils/useDebounce';

const PILL_COLORS = [
    { bg: 'rgba(99,102,241,0.18)', border: 'rgba(99,102,241,0.45)', text: '#a5b4fc' },   // indigo
    { bg: 'rgba(244,114,182,0.18)', border: 'rgba(244,114,182,0.45)', text: '#f9a8d4' },  // pink
    { bg: 'rgba(52,211,153,0.18)', border: 'rgba(52,211,153,0.45)', text: '#6ee7b7' },    // emerald
    { bg: 'rgba(251,191,36,0.18)', border: 'rgba(251,191,36,0.45)', text: '#fcd34d' },    // amber
    { bg: 'rgba(96,165,250,0.18)', border: 'rgba(96,165,250,0.45)', text: '#93c5fd' },    // blue
    { bg: 'rgba(167,139,250,0.18)', border: 'rgba(167,139,250,0.45)', text: '#c4b5fd' },  // violet
    { bg: 'rgba(248,113,113,0.18)', border: 'rgba(248,113,113,0.45)', text: '#fca5a5' },   // red
    { bg: 'rgba(45,212,191,0.18)', border: 'rgba(45,212,191,0.45)', text: '#5eead4' },    // teal
];

function pillColor(index: number) {
    return PILL_COLORS[index % PILL_COLORS.length];
}

/* ── Props ── */
export interface TagsInputProps<T> {
    selectedItems: T[];
    onSelectedItemsChange: (items: T[]) => void;
    availableItems: T[];
    getItemLabel?: (item: T) => string;
    getItemKey?: (item: T) => string;
    label?: string;
    placeholder?: string;
    emptyStateText?: string;
    noResultsText?: string;
    maxSuggestions?: number;
    disabled?: boolean;
    debounceMs?: number;
    /** If true, suggestions render via a portal with fixed positioning (avoids clipping). Default true. */
    portal?: boolean;
    /** If availableItems.length exceeds this, results are hard-limited for performance. Default 500. */
    virtualizeThreshold?: number;
    /** Optional custom chip renderer. Receives item, color object, and remove handler. */
    renderChip?: (item: T, color: typeof PILL_COLORS[number], onRemove: () => void) => React.ReactNode;
    /** Debug mode: enable instrumentation logs and mount counts. Default false. */
    debug?: boolean;
}

/* --- Runtime metrics helper (useful for debugging leaks) --- */
function ensureMetrics() {
    const w = window as any;
    if (!w.__tagsInputMetrics) {
        w.__tagsInputMetrics = { mounts: 0, unmounts: 0, timeouts: 0, rafs: 0, listeners: 0 };
    }
    return w.__tagsInputMetrics as { mounts: number; unmounts: number; timeouts: number; rafs: number; listeners: number };
}

function incMetric(key: keyof ReturnType<typeof ensureMetrics>) {
    const m = ensureMetrics();
    m[key] = (m[key] || 0) + 1;
}

function decMetric(key: keyof ReturnType<typeof ensureMetrics>) {
    const m = ensureMetrics();
    m[key] = Math.max(0, (m[key] || 1) - 1);
}

const TagsInput = <T,>({
    selectedItems,
    onSelectedItemsChange,
    availableItems,
    getItemLabel,
    getItemKey,
    label = 'Tags',
    placeholder = 'Zoek en selecteer...',
    emptyStateText = 'Geen items geselecteerd',
    noResultsText = 'Geen resultaten',
    maxSuggestions = 8,
    disabled = false,
    debounceMs = 200,
    portal = true,
    virtualizeThreshold = 500,
    renderChip,
    debug = false,
}: TagsInputProps<T>) => {
    const [searchInput, setSearchInput] = useState('');
    const debouncedSearch = useDebounce(searchInput, debounceMs);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);

    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const blurTimeoutRef = useRef<number | undefined>(undefined);
    const rafRef = useRef<number | null>(null);
    const lastTouchKeyRef = useRef<string | null>(null);
    const lastTouchClearRef = useRef<number | undefined>(undefined);
    const suppressClickRef = useRef(false);
    const suppressClickClearRef = useRef<number | undefined>(undefined);

    /* ── Dropdown position (for portal mode) ── */
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

    // Track last computed dropdown position to avoid unnecessary setState
    const lastDropdownPosRef = useRef(dropdownPos);

    // Throttled update using requestAnimationFrame to avoid heavy work on scroll/resize
    // (note: we re-declare updateDropdownPos to capture lastDropdownPosRef)
    const updateDropdownPos = useCallback(() => {
        if (rafRef.current != null) return; // already scheduled
        rafRef.current = window.requestAnimationFrame(() => {
            rafRef.current = null;
            decMetric('rafs');
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const newPos = {
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
            };
            const last = lastDropdownPosRef.current;
            // shallow compare to avoid state churn
            if (last.top !== newPos.top || last.left !== newPos.left || last.width !== newPos.width) {
                lastDropdownPosRef.current = newPos;
                setDropdownPos(newPos);
            }
        });
        incMetric('rafs');
    }, []);

    // Recompute position when suggestions become visible or on scroll/resize
    useLayoutEffect(() => {
        if (!showSuggestions || !portal) return;
        updateDropdownPos();

        const onUpdate = () => updateDropdownPos();
        window.addEventListener('scroll', onUpdate, true);
        window.addEventListener('resize', onUpdate);
        // instrumentation
        incMetric('listeners');
        incMetric('listeners');
        return () => {
            window.removeEventListener('scroll', onUpdate, true);
            window.removeEventListener('resize', onUpdate);
            // cancel any pending RAF
            if (rafRef.current != null) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
                decMetric('rafs');
            }
            // instrumentation
            decMetric('listeners');
            decMetric('listeners');
        };
    }, [showSuggestions, portal, updateDropdownPos]);

    /* ── Resolvers ── */
    const resolveLabel = useCallback(
        (item: T) => (getItemLabel ? getItemLabel(item) : String(item)),
        [getItemLabel],
    );

    const resolveKey = useCallback(
        (item: T) => (getItemKey ? getItemKey(item) : String(item)),
        [getItemKey],
    );

    /* ── Derived lists ── */
    const selectedKeys = useMemo(
        () => new Set(selectedItems.map(resolveKey)),
        [selectedItems, resolveKey],
    );

    const unselectedOptions = useMemo(
        () => availableItems.filter(item => !selectedKeys.has(resolveKey(item))),
        [availableItems, selectedKeys, resolveKey],
    );

    const isLargeList = availableItems.length > virtualizeThreshold;

    const filteredOptions = useMemo(() => {
        const query = debouncedSearch.trim().toLowerCase();
        const base = query.length === 0
            ? unselectedOptions
            : unselectedOptions.filter(item =>
                resolveLabel(item).toLowerCase().includes(query),
            );
        return base.slice(0, isLargeList ? Math.min(maxSuggestions, 20) : maxSuggestions);
    }, [unselectedOptions, debouncedSearch, maxSuggestions, resolveLabel, isLargeList]);

    const totalUnfilteredCount = useMemo(() => {
        const query = debouncedSearch.trim().toLowerCase();
        if (query.length === 0) return unselectedOptions.length;
        return unselectedOptions.filter(item =>
            resolveLabel(item).toLowerCase().includes(query),
        ).length;
    }, [unselectedOptions, debouncedSearch, resolveLabel]);

    useEffect(() => {
        if (highlightedIndex >= filteredOptions.length) {
            setHighlightedIndex(0);
        }
    }, [filteredOptions, highlightedIndex]);

    /* ── Handlers ── */
    const handleAdd = useCallback(
        (item: T) => {
            onSelectedItemsChange([...selectedItems, item]);
            setSearchInput('');
            setShowSuggestions(true);
            setHighlightedIndex(0);
            inputRef.current?.focus();
        },
        [selectedItems, onSelectedItemsChange],
    );

    const handleRemove = useCallback(
        (item: T) => {
            const key = resolveKey(item);
            onSelectedItemsChange(selectedItems.filter(i => resolveKey(i) !== key));
        },
        [selectedItems, onSelectedItemsChange, resolveKey],
    );

    const handleBlur = useCallback(() => {
        // clear existing timeout if any
        if (blurTimeoutRef.current) {
            window.clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = undefined;
            decMetric('timeouts');
        }
        blurTimeoutRef.current = window.setTimeout(() => {
            setShowSuggestions(false);
            // when the timeout executes we should decrement the counter
            decMetric('timeouts');
            blurTimeoutRef.current = undefined;
        }, 200);
        incMetric('timeouts');
    }, []);

    const handleFocus = useCallback(() => {
        if (blurTimeoutRef.current) {
            window.clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = undefined;
            decMetric('timeouts');
        }
        if (!disabled) {
            setShowSuggestions(true);
        }
    }, [disabled]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (!showSuggestions || filteredOptions.length === 0) return;

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    setHighlightedIndex(prev => (prev + 1) % filteredOptions.length);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    setHighlightedIndex(prev =>
                        (prev - 1 + filteredOptions.length) % filteredOptions.length,
                    );
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (filteredOptions[highlightedIndex]) {
                        handleAdd(filteredOptions[highlightedIndex]);
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    setShowSuggestions(false);
                    break;
            }
        },
        [showSuggestions, filteredOptions, highlightedIndex, handleAdd],
    );

    // Close dropdown when clicking outside (for portal mode)
    useEffect(() => {
        if (!showSuggestions || !portal) return;
        const handleOutsideClick = (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick, true);
        incMetric('listeners');
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick, true);
            // decrement the listener metric when cleaning up
            decMetric('listeners');
        };
    }, [showSuggestions, portal]);

    // Replace verbose debug effects with a single rate-limited logger that only logs when values change
    const lastLogRef = useRef({
        selectedItems: '' as string,
        filteredOptions: '' as string,
        debouncedSearch: '' as string,
        showSuggestions: '' as string,
        highlightedIndex: '' as string,
        lastTs: 0,
    });

    useEffect(() => {
        if (!debug) return;
        const now = Date.now();
        // rate-limit logs to once per 500ms
        if (now - lastLogRef.current.lastTs < 500) return;

        // serialize shallowly
        const sel = JSON.stringify(selectedItems);
        const filt = JSON.stringify(filteredOptions.map(resolveLabel));
        const deb = String(debouncedSearch);
        const show = String(showSuggestions);
        const hi = String(highlightedIndex);

        const parts: string[] = [];
        if (sel !== lastLogRef.current.selectedItems) {
            parts.push(`[selectedItems] ${sel}`);
            lastLogRef.current.selectedItems = sel;
        }
        if (filt !== lastLogRef.current.filteredOptions) {
            parts.push(`[filteredOptions] ${filt}`);
            lastLogRef.current.filteredOptions = filt;
        }
        if (deb !== lastLogRef.current.debouncedSearch) {
            parts.push(`[debouncedSearch] ${deb}`);
            lastLogRef.current.debouncedSearch = deb;
        }
        if (show !== lastLogRef.current.showSuggestions) {
            parts.push(`[showSuggestions] ${show}`);
            lastLogRef.current.showSuggestions = show;
        }
        if (hi !== lastLogRef.current.highlightedIndex) {
            parts.push(`[highlightedIndex] ${hi}`);
            lastLogRef.current.highlightedIndex = hi;
        }

        if (parts.length > 0) {
            console.log('[TagsInput]', parts.join(' | '));
            lastLogRef.current.lastTs = now;
        }
    }, [selectedItems, filteredOptions, debouncedSearch, showSuggestions, highlightedIndex, debug, resolveLabel]);

    // Clear any pending timeout / raf on unmount to avoid memory leaks
    useEffect(() => {
        return () => {
            if (blurTimeoutRef.current) {
                window.clearTimeout(blurTimeoutRef.current);
                blurTimeoutRef.current = undefined;
                decMetric('timeouts');
            }
            if (rafRef.current != null) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
                decMetric('rafs');
            }
            if (lastTouchClearRef.current) {
                window.clearTimeout(lastTouchClearRef.current);
                lastTouchClearRef.current = undefined;
                lastTouchKeyRef.current = null;
            }
            if (suppressClickClearRef.current) {
                window.clearTimeout(suppressClickClearRef.current);
                suppressClickClearRef.current = undefined;
            }
            suppressClickRef.current = false;
            // a component unmount counts too
            decMetric('mounts');
            incMetric('unmounts');
        };
    }, []);

    // Track mounts for metrics
    useEffect(() => {
        incMetric('mounts');
        if (debug) console.log('[TagsInput] metrics after mount', (window as any).__tagsInputMetrics);
        return () => {
            if (debug) console.log('[TagsInput] metrics before unmount', (window as any).__tagsInputMetrics);
        };
    }, [debug]);

    const hasSelected = selectedItems.length > 0;
    const allAssigned = unselectedOptions.length === 0;
    const listboxId = `tags-listbox-${label?.replace(/\s/g, '-') ?? 'default'}`;

    const markLastTouchKey = useCallback((key: string) => {
        lastTouchKeyRef.current = key;
        if (lastTouchClearRef.current) {
            window.clearTimeout(lastTouchClearRef.current);
        }
        lastTouchClearRef.current = window.setTimeout(() => {
            lastTouchKeyRef.current = null;
            lastTouchClearRef.current = undefined;
        }, 500);
    }, []);

    const suppressClickOnce = useCallback(() => {
        suppressClickRef.current = true;
        if (suppressClickClearRef.current) {
            window.clearTimeout(suppressClickClearRef.current);
        }
        suppressClickClearRef.current = window.setTimeout(() => {
            suppressClickRef.current = false;
            suppressClickClearRef.current = undefined;
        }, 350);
    }, []);

    /* ── Suggestions dropdown content ── */
    const renderDropdown = () => {
        if (!showSuggestions || disabled) return null;

        const content = (
            <div
                className={`tags-suggestions-dropdown${portal ? ' tags-suggestions-portal' : ''}`}
                role="listbox"
                id={listboxId}
                style={portal ? {
                    position: 'fixed',
                    top: dropdownPos.top,
                    left: dropdownPos.left,
                    width: dropdownPos.width,
                    zIndex: 9999,
                } : undefined}
            >
                {filteredOptions.length > 0 ? (
                    <>
                        {filteredOptions.map((item, index) => {
                            const key = resolveKey(item);
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    role="option"
                                    id={`${listboxId}-opt-${index}`}
                                    onMouseDown={e => e.preventDefault()}
                                    onPointerDown={e => {
                                        if (e.pointerType === 'touch' || e.pointerType === 'pen') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            markLastTouchKey(key);
                                            suppressClickOnce();
                                            handleAdd(item);
                                        }
                                    }}
                                    onClick={() => {
                                        if (suppressClickRef.current) {
                                            return;
                                        }
                                        if (lastTouchKeyRef.current === key) {
                                            lastTouchKeyRef.current = null;
                                            return;
                                        }
                                        handleAdd(item);
                                    }}
                                    className={`tags-suggestion-item${index === highlightedIndex ? ' is-highlighted' : ''}`}
                                    aria-selected={index === highlightedIndex}
                                >
                                    <IonIcon icon={addCircleOutline} className="tags-suggestion-icon" />
                                    <span>{resolveLabel(item)}</span>
                                </button>
                            );
                        })}
                        {totalUnfilteredCount > filteredOptions.length && (
                            <div className="tags-suggestion-more">
                                <IonText color="medium">
                                    +{totalUnfilteredCount - filteredOptions.length} meer — typ om te verfijnen
                                </IonText>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="tags-suggestion-empty">
                        <IonText color="medium">
                            {allAssigned ? 'Alle opties zijn al geselecteerd' : noResultsText}
                        </IonText>
                    </div>
                )}
            </div>
        );

        if (portal) {
            return ReactDOM.createPortal(content, document.body);
        }
        return content;
    };

    return (
        <div
            ref={containerRef}
            className={`tags-input-container${disabled ? ' is-disabled' : ''}`}
        >
            {label && <span className="tags-input-label">{label}</span>}

            {/* Selected chips */}
            <div className="tags-chips-area" aria-live="polite">
                {hasSelected ? (
                    selectedItems.map((item, idx) => {
                        const color = pillColor(idx);

                        if (renderChip) {
                            return (
                                <React.Fragment key={resolveKey(item)}>
                                    {renderChip(item, color, () => handleRemove(item))}
                                </React.Fragment>
                            );
                        }

                        return (
                            <span
                                key={resolveKey(item)}
                                className="tags-chip"
                                style={{
                                    background: color.bg,
                                    borderColor: color.border,
                                    color: color.text,
                                }}
                            >
                                <span className="tags-chip-label">{resolveLabel(item)}</span>
                                {!disabled && (
                                    <button
                                        type="button"
                                        className="tags-chip-remove"
                                        onClick={() => handleRemove(item)}
                                        aria-label={`Verwijder ${resolveLabel(item)}`}
                                        style={{ color: color.text }}
                                    >
                                        <IonIcon icon={closeCircle} />
                                    </button>
                                )}
                            </span>
                        );
                    })
                ) : (
                    <span className="tags-empty-state">
                        <IonText color="medium">{emptyStateText}</IonText>
                    </span>
                )}
            </div>

            {/* Search input */}
            {!disabled && (
                <div className="tags-search-wrapper">
                    <div className="tags-search-inner">
                        <input
                            ref={inputRef}
                            value={searchInput}
                            placeholder={placeholder}
                            onChange={e => setSearchInput(e.target.value)}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            className="tags-search-input"
                            disabled={disabled}
                            role="combobox"
                            aria-expanded={showSuggestions}
                            aria-controls={listboxId}
                            aria-activedescendant={
                                showSuggestions && filteredOptions.length > 0
                                    ? `${listboxId}-opt-${highlightedIndex}`
                                    : undefined
                            }
                            autoComplete="off"
                        />
                        <IonIcon
                            icon={chevronDownOutline}
                            className={`tags-search-chevron${showSuggestions ? ' is-open' : ''}`}
                        />
                    </div>
                </div>
            )}

            {/* Suggestions dropdown */}
            {renderDropdown()}
        </div>
    );
};

export default TagsInput;

